import { useAppDispatch } from 'app/hooks';
import { MIN_SPACE_DIM_FEET, STRUCTURE_GRID_SIZE } from 'data/constants';
import {
  AttributeType,
  FootprintPolygonsType,
  GridSpaceDataType,
  LevelType,
  PartialWithRequired,
  PositionAndDimensionsType,
  SpaceType
} from 'data/types';
import {
  beginUndoBatch,
  clearSpaceExtensionRecords,
  endUndoBatch,
  updateStructureItem,
  updateStructureItemAttribute
} from 'features/structure';
import React, { useCallback, useRef, useState } from 'react';
import { HYBRID_LEFTOVER_HOLE_MESSAGE } from 'utils/apply-carve-leftover';
import { constrainResizeToFootprints } from 'utils/footprint-polygons';
import {
  calculateCornerResizePosition,
  calculateEdgeResizePosition,
  calculateResizedPosition,
  calculateSharedEdgeResizePositions,
  constrainResizeToBoundaries,
  type ResizeDirection
} from 'utils/grid';
import {
  commitUserSpaceGeometryAndLeftovers,
  gridSpacesExcludingLeftovers
} from 'utils/leftover-space-sync';
import {
  filterSpacesOverlappingRect,
  rectToVertices
} from 'utils/space-polygon';
import { normalizeGridPosition } from 'utils/structure';
import { warningModal } from 'utils/ui';

type UseGridSpaceResizeParams = {
  gridSpace: GridSpaceDataType;
  allGridSpaces: GridSpaceDataType[];
  effectiveFootprints: FootprintPolygonsType;
  effectiveZoom: number;
  isSelected: boolean;
  leftoverIds: ReadonlySet<string>;
  rebuildLeftoverOnResize: boolean;
  spaceLevel: LevelType | null | undefined;
  structure: LevelType[];
  autoLayoutSpaceIds: readonly string[];
  catalogAttributes: AttributeType[];
};

function snapToGrid(value: number): number {
  return Math.round(value / STRUCTURE_GRID_SIZE);
}

export function useGridSpaceResize({
  gridSpace,
  allGridSpaces,
  effectiveFootprints,
  effectiveZoom,
  isSelected,
  leftoverIds,
  rebuildLeftoverOnResize,
  spaceLevel,
  structure,
  autoLayoutSpaceIds,
  catalogAttributes
}: UseGridSpaceResizeParams) {
  const dispatch = useAppDispatch();
  const [isResizing, setIsResizing] = useState(false);
  const startPositionRef = useRef<{ x: number; y: number } | null>(null);
  const initialPositionRef = useRef<PositionAndDimensionsType | null>(null);
  const currentPositionRef = useRef<PositionAndDimensionsType | null>(null);
  const hasCommittedGeometryRef = useRef(false);

  const handleResize = useCallback(
    (
      spaceId: string,
      newPosition: PositionAndDimensionsType,
      direction: ResizeDirection
    ) => {
      const isEdgeResize =
        direction === 'top' ||
        direction === 'right' ||
        direction === 'bottom' ||
        direction === 'left';

      if (isEdgeResize && !isSelected && !rebuildLeftoverOnResize) {
        const sharedEdgeResizePositions = calculateSharedEdgeResizePositions({
          activeSpace: gridSpace,
          allGridSpaces,
          requestedActivePosition: newPosition,
          edge: direction,
          footprintPolygons: effectiveFootprints
        });

        if (sharedEdgeResizePositions) {
          sharedEdgeResizePositions.forEach((position, affectedSpaceId) => {
            const quantizedPosition = {
              ...position,
              width: Math.max(MIN_SPACE_DIM_FEET, Math.round(position.width)),
              height: Math.max(MIN_SPACE_DIM_FEET, Math.round(position.height))
            };

            const updatedSpace: PartialWithRequired<SpaceType, 'uniqueId'> = {
              uniqueId: affectedSpaceId,
              position: quantizedPosition,
              length: quantizedPosition.height,
              width: quantizedPosition.width,
              vertices: rectToVertices(
                quantizedPosition.width,
                quantizedPosition.height
              )
            };

            dispatch(updateStructureItem(updatedSpace));
            dispatch(
              updateStructureItemAttribute({
                uniqueId: affectedSpaceId,
                attributeCode: 'square_footage',
                value: Math.round(
                  quantizedPosition.height * quantizedPosition.width
                )
              })
            );
          });

          hasCommittedGeometryRef.current = true;
          return;
        }
      }

      const finalPosition = calculateResizedPosition({
        gridSpace,
        allGridSpaces,
        newPosition
      });

      const otherSpaces = (
        rebuildLeftoverOnResize
          ? gridSpacesExcludingLeftovers(allGridSpaces, leftoverIds)
          : allGridSpaces
      ).filter((s) => s.id !== gridSpace.id);
      const obstructingSpaces = filterSpacesOverlappingRect(
        otherSpaces,
        finalPosition
      );
      const obstructingSpacesRects = obstructingSpaces.map((s) => ({
        ...normalizeGridPosition(s.position)
      }));

      let resolvedPosition = constrainResizeToBoundaries(
        finalPosition,
        obstructingSpacesRects,
        direction
      );

      resolvedPosition = constrainResizeToFootprints(
        resolvedPosition,
        effectiveFootprints,
        direction
      );

      const quantizedPosition = {
        ...resolvedPosition,
        width: Math.max(MIN_SPACE_DIM_FEET, Math.round(resolvedPosition.width)),
        height: Math.max(
          MIN_SPACE_DIM_FEET,
          Math.round(resolvedPosition.height)
        )
      };

      const noOverlapAfter =
        filterSpacesOverlappingRect(otherSpaces, quantizedPosition).length ===
        0;

      if (!noOverlapAfter) {
        return;
      }

      const resizedVertices = rectToVertices(
        quantizedPosition.width,
        quantizedPosition.height
      );
      const squareFootage = Math.round(
        quantizedPosition.height * quantizedPosition.width
      );

      if (spaceLevel) {
        const leftoverResult = commitUserSpaceGeometryAndLeftovers(
          dispatch,
          structure,
          spaceLevel,
          autoLayoutSpaceIds,
          catalogAttributes,
          {
            uniqueId: spaceId,
            position: { x: quantizedPosition.x, y: quantizedPosition.y },
            width: quantizedPosition.width,
            length: quantizedPosition.height,
            vertices: resizedVertices,
            squareFootage
          }
        );
        if (!leftoverResult.ok) {
          if (leftoverResult.reason === 'hole') {
            warningModal(HYBRID_LEFTOVER_HOLE_MESSAGE);
          }
          return;
        }
        if (leftoverResult.applied) {
          hasCommittedGeometryRef.current = true;
          return;
        }
      }

      const updatedSpace: PartialWithRequired<SpaceType, 'uniqueId'> = {
        uniqueId: spaceId,
        position: quantizedPosition,
        length: quantizedPosition.height,
        width: quantizedPosition.width,
        vertices: resizedVertices
      };

      dispatch(updateStructureItem(updatedSpace));
      dispatch(
        updateStructureItemAttribute({
          uniqueId: spaceId,
          attributeCode: 'square_footage',
          value: squareFootage
        })
      );
      hasCommittedGeometryRef.current = true;
    },
    [
      dispatch,
      gridSpace,
      allGridSpaces,
      effectiveFootprints,
      isSelected,
      rebuildLeftoverOnResize,
      leftoverIds,
      spaceLevel,
      autoLayoutSpaceIds,
      structure,
      catalogAttributes
    ]
  );

  const handleResizeStart = useCallback(
    (e: React.MouseEvent, handle: 'nw' | 'ne' | 'sw' | 'se') => {
      e.preventDefault();
      e.stopPropagation();

      setIsResizing(true);
      startPositionRef.current = { x: e.clientX, y: e.clientY };
      initialPositionRef.current = gridSpace.position;
      hasCommittedGeometryRef.current = false;
      dispatch(beginUndoBatch());
      let batchOpen = true;
      const closeBatch = () => {
        if (!batchOpen) return;
        batchOpen = false;
        dispatch(endUndoBatch());
      };

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!initialPositionRef.current || !startPositionRef.current) {
          return;
        }

        const deltaX =
          (moveEvent.clientX - startPositionRef.current.x) / effectiveZoom;
        const deltaY =
          (moveEvent.clientY - startPositionRef.current.y) / effectiveZoom;

        const newPosition = calculateCornerResizePosition({
          handle,
          deltaX,
          deltaY,
          initialPosition: initialPositionRef.current,
          currentPosition: gridSpace.position,
          snapToGrid
        });

        currentPositionRef.current = newPosition;
        handleResize(gridSpace.id, newPosition, handle);
      };

      const handleMouseUp = () => {
        if (hasCommittedGeometryRef.current) {
          dispatch(clearSpaceExtensionRecords({ spaceId: gridSpace.spaceId }));
        }
        closeBatch();
        setIsResizing(false);
        startPositionRef.current = null;
        initialPositionRef.current = null;
        currentPositionRef.current = null;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [
      dispatch,
      gridSpace.id,
      gridSpace.spaceId,
      gridSpace.position,
      handleResize,
      effectiveZoom
    ]
  );

  const handleEdgeResizeStart = useCallback(
    (e: React.MouseEvent, edge: 'top' | 'right' | 'bottom' | 'left') => {
      e.preventDefault();
      e.stopPropagation();

      setIsResizing(true);
      startPositionRef.current = { x: e.clientX, y: e.clientY };
      initialPositionRef.current = gridSpace.position;
      hasCommittedGeometryRef.current = false;
      dispatch(beginUndoBatch());
      let batchOpen = true;
      const closeBatch = () => {
        if (!batchOpen) return;
        batchOpen = false;
        dispatch(endUndoBatch());
      };

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!initialPositionRef.current || !startPositionRef.current) {
          return;
        }

        const deltaX =
          (moveEvent.clientX - startPositionRef.current.x) / effectiveZoom;
        const deltaY =
          (moveEvent.clientY - startPositionRef.current.y) / effectiveZoom;

        const newPosition = calculateEdgeResizePosition({
          edge,
          deltaX,
          deltaY,
          initialPosition: initialPositionRef.current,
          currentPosition: gridSpace.position,
          snapToGrid
        });

        currentPositionRef.current = newPosition;
        handleResize(gridSpace.id, newPosition, edge);
      };

      const handleMouseUp = () => {
        if (hasCommittedGeometryRef.current) {
          dispatch(clearSpaceExtensionRecords({ spaceId: gridSpace.spaceId }));
        }
        closeBatch();
        setIsResizing(false);
        startPositionRef.current = null;
        initialPositionRef.current = null;
        currentPositionRef.current = null;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [
      dispatch,
      gridSpace.id,
      gridSpace.spaceId,
      gridSpace.position,
      handleResize,
      effectiveZoom
    ]
  );

  return {
    isResizing,
    handleResizeStart,
    handleEdgeResizeStart
  };
}
